// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * An RFC 4180 CSV writer.
 *
 * Written rather than imported because the fields it writes are not
 * first-party: every report row quotes a passage out of the user's own files.
 * Four things go wrong with such a field and all four go wrong quietly.
 *
 * 1. A LINE BREAK inside a quoted field is legal CSV that most readers handle,
 *    but somebody counting rows with `wc -l` then sees one record split across
 *    nine lines. This tool's whole output is a count of rows, so a file whose
 *    row count depends on how you open it is unverifiable. Control characters
 *    become visible escapes and ONE RECORD IS ALWAYS ONE PHYSICAL LINE.
 * 2. FORMULA INJECTION. A field beginning `=`, `+`, `-` or `@` is executed by
 *    Excel, LibreOffice and Sheets on open — and these fields contain arbitrary
 *    text out of a training corpus.
 * 3. THE SEPARATOR IS NOT UNIVERSAL. Excel in German, French and Spanish
 *    locales reads a comma-separated file as a single column, so a field
 *    containing `;` is quoted too.
 * 4. NO BOM AND EXCEL MOJIBAKES the first non-ASCII character it meets.
 *
 * There is deliberately no character-class regex for the control characters:
 * the obvious way to write one is to paste the characters themselves, and a
 * literal control byte in source is invisible in review and in a diff. A
 * source-hygiene gate fails the build on one. The set is a code-point test.
 */

/** True for C0 controls and DEL. Tab, CR and LF included: they are escaped, not passed through. */
export function isControl(code: number): boolean {
  return code < 0x20 || code === 0x7f;
}

function hasControl(value: string): boolean {
  for (let i = 0; i < value.length; i++) if (isControl(value.charCodeAt(i))) return true;
  return false;
}

const NAMED_ESCAPES = new Map<number, string>([
  [0x0d, '\\r'],
  [0x0a, '\\n'],
  [0x09, '\\t'],
]);

export function escapeControls(value: string): string {
  let out = '';
  for (const ch of value) {
    const code = ch.codePointAt(0) as number;
    const named = NAMED_ESCAPES.get(code);
    if (named !== undefined) out += named;
    else if (isControl(code)) out += `\\x${code.toString(16).padStart(2, '0')}`;
    else out += ch;
  }
  return out;
}

/**
 * A field that is nothing but a number.
 *
 * This exists to stop the injection guard corrupting real data. `-` and `+`
 * both begin a formula, so the naive guard prefixes an apostrophe onto every
 * value starting with one — and a column of containment deltas then arrives in
 * Excel as TEXT and no longer sums. Almost every column this tool writes is
 * numeric, so the naive guard would corrupt nearly the whole file.
 *
 * Exempting complete numeric literals is safe because one cannot also be a
 * formula: Excel evaluates `-3.5` to -3.5 either way. `-1+1` and `@SUM(1+1)`
 * fail this test and are still guarded.
 */
function isPlainNumber(text: string): boolean {
  return /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(text);
}

export function csvField(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) return '';
  let text = escapeControls(String(value));

  // The guard runs on the ESCAPED text: a field that began with a tab followed
  // by `=` is still a formula to Excel, and after escaping the tab is two
  // visible characters rather than one invisible one.
  const dangerous = /^[=+\-@]/.test(text) && !isPlainNumber(text);
  if (dangerous) text = `'${text}`;

  const needsQuotes = dangerous || /[",;]/.test(text) || hasControl(text) || text !== text.trim();
  if (!needsQuotes) return text;
  return `"${text.replace(/"/g, '""')}"`;
}

export function csvRow(cells: (string | number | boolean | null | undefined)[]): string {
  return cells.map(csvField).join(',');
}

/** CRLF because RFC 4180 says so and because Excel on Windows is the likeliest destination. */
export function csvText(
  header: string[],
  rows: (string | number | boolean | null | undefined)[][],
  comments: string[] = [],
): string {
  const preamble = comments.map((line) => `# ${escapeControls(line)}`);
  return [...preamble, csvRow(header), ...rows.map(csvRow)].join('\r\n') + '\r\n';
}

export function csvBlob(
  header: string[],
  rows: (string | number | boolean | null | undefined)[][],
  comments: string[] = [],
): Blob {
  // The BOM is added here rather than in `csvText`, so the string form stays
  // comparable in a test without one.
  return new Blob([String.fromCharCode(0xfeff), csvText(header, rows, comments)], {
    type: 'text/csv;charset=utf-8',
  });
}
